import React from "react"
import ReactApexChart from "react-apexcharts"
import { useTranslation } from "react-i18next"
import countries from "i18n-iso-countries"

function trendFilterProperty(trendType) {
    if (trendType === "rising") return "priceTrendRising"
    if (trendType === "falling") return "priceTrendFalling"
    return "priceTrendUnchanged"
}

function trendColorClass(averageTrend) {
    if (averageTrend > 0) return "text-success"
    if (averageTrend < 0) return "text-danger"
    return "text-secondary"
}

function PPDonutTrendBox({ value, count, text, explanation, startColor, endColor, onOpenClick, showMore, trendType, selectedShopId, shopIdsForCurrentLang }) {
    const config = {
        series: [value],
        options: {
            grid: {
                padding: {
                    top: 0,
                    bottom: 0,
                    left: 0,
                    right: 0
                }
            },
            chart: {
                type: "radialBar",
                sparkline: {
                    enabled: true
                }
            },
            plotOptions: {
                radialBar: {
                    track: {
                        background: "lightgray",
                        strokeWidth: "60%",
                        margin: 0
                    },
                    hollow: {
                        margin: 0,
                        size: "70%"
                    },
                    dataLabels: {
                        name: {
                            color: "#000",
                            fontSize: "12px",
                            show: false,
                            offsetY: -20
                        },
                        value: {
                            color: "#000000",
                            fontSize: "1.25rem",
                            show: true,
                            offsetY: 5
                        },
                        total: {
                            show: true,
                            label: "",
                            formatter: function (w) {
                                return w.config.series[0]?.toFixed(1) + "%"
                            }
                        }
                    }
                }
            },
            fill: {
                colors: [startColor],
                type: "gradient",
                gradient: {
                    shade: "dark",
                    gradientToColors: [endColor],
                    shadeIntensity: 1,
                    opacityFrom: 1,
                    opacityTo: 1,
                    stops: [0, 100, 100, 100]
                }
            }
        }
    }

    return (
        <div className="col-lg-4 col-md-6 col-xs-12 connectedSortable ui-sortable">
            <div className="info-box bg-white min-height ui-sortable-handle shadow">
                <div className="d-flex flex-grow-1">
                    <div className="flex-grow-0">
                        <ReactApexChart options={config.options} series={config.series} type="radialBar" width={200} />
                    </div>
                    <div className="d-flex flex-column flex-fill justify-content-around align-items-center">
                        <h5 className="text-center">{text}</h5>
                        <div className="text-center">
                            {trendType ? (
                                <a 
                                    href={(() => {
                                        const filterProperty = trendFilterProperty(trendType)
                                        let href = `/pricetable/generic/${filterProperty}`
                                        // Wenn ein spezifischer Shop ausgewählt ist, verwende diesen
                                        // Wenn "all" ausgewählt ist, keine shopId übergeben - die Preistabelle verwendet das Preset
                                        if (selectedShopId && selectedShopId !== "all") {
                                            href += `?shopId=${selectedShopId}`
                                        }
                                        return href
                                    })()}
                                    className="pp-price-trend-link"
                                >
                                    <span className="h4 pp-price-trend-link-span">
                                        {count}
                                        <span className="pp-price-trend-arrow">⟶</span>
                                    </span>
                                </a>
                            ) : (
                                <span className="h4">{count}</span>
                            )}
                            <span className="text-muted ms-1 pp-price-trend-percentage">
                                ({value?.toFixed(1) || 0}%)
                            </span>
                        </div>
                        {explanation && (
                            <div className="text-center small text-muted px-2 pp-price-trend-explanation">
                                {explanation}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    )
}


function TrendOverview({ trendDistribution }) {
    const [t] = useTranslation()

    return (
        <div>
            <div className="row">
                <div className="col-md-12">
                    <h5>{t("dashboard.trend_distribution") || "Trend-Verteilung"}</h5>
                    <table className="table table-striped pp-price-trend-table">
                        <thead>
                            <tr>
                                <th className="pp-price-trend-table-header-trend">{t("dashboard.trend") || "Trend"}</th>
                                <th className="pp-price-trend-table-header-count">{t("single.count") || "Anzahl"}</th>
                                <th className="pp-price-trend-table-header-proportion">{t("single.proportion") || "Anteil"}</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td className="pp-price-trend-table-cell-trend">
                                    <i className="fas fa-arrow-up text-success me-2"></i>
                                    {t("dashboard.trend_rising") || "Steigend"}
                                </td>
                                <td className="pp-price-trend-table-cell-count">{trendDistribution.risingCount || 0}</td>
                                <td className="pp-price-trend-table-cell-proportion">{(trendDistribution.risingPercent || 0).toFixed(1)}%</td>
                            </tr>
                            <tr>
                                <td className="pp-price-trend-table-cell-trend">
                                    <i className="fas fa-arrow-down text-danger me-2"></i>
                                    {t("dashboard.trend_falling") || "Fallend"}
                                </td>
                                <td className="pp-price-trend-table-cell-count">{trendDistribution.fallingCount || 0}</td>
                                <td className="pp-price-trend-table-cell-proportion">{(trendDistribution.fallingPercent || 0).toFixed(1)}%</td>
                            </tr>
                            <tr>
                                <td className="pp-price-trend-table-cell-trend">
                                    <i className="fas fa-minus text-secondary me-2"></i>
                                    {t("dashboard.trend_unchanged") || "Unverändert"}
                                </td>
                                <td className="pp-price-trend-table-cell-count">{trendDistribution.unchangedCount || 0}</td>
                                <td className="pp-price-trend-table-cell-proportion">{(trendDistribution.unchangedPercent || 0).toFixed(1)}%</td>
                            </tr>
                            <tr className="fw-bold">
                                <td className="pp-price-trend-table-cell-trend">{t("dashboard.total") || "Gesamt"}</td>
                                <td className="pp-price-trend-table-cell-count">{trendDistribution.totalProducts || 0}</td>
                                <td className="pp-price-trend-table-cell-proportion">100%</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    )
}

function TrendByCategoryTable({ items, activeTrendType, selectedShopId, selectedShopName, selectedLang, shopIdsForCurrentLang }) {
    const [t] = useTranslation()

    if (!items || items.length === 0) {
        return <p className="text-center text-muted">{t("single.no_results") || "Keine Daten verfügbar"}</p>
    }

    const rows = items.map((item, index) => (
        <TrendByCategoryRow
            key={index + "_" + JSON.stringify(item)}
            rowNumber={index + 1}
            categoryName={item.categoryName}
            risingCount={item.risingCount}
            fallingCount={item.fallingCount}
            unchangedCount={item.unchangedCount}
            totalCount={item.totalCount}
            averageTrend={item.averageTrend}
            activeTrendType={activeTrendType}
            selectedShopId={selectedShopId}
            selectedShopName={selectedShopName}
            selectedLang={selectedLang}
        />
    ))

    return (
        <div className="table-responsive">
            <table className="table table-striped dt-responsive">
                <thead>
                    <tr>
                        <th className={"width-10"}>#</th>
                        <th>{t("single.category")}</th>
                        <th>{t("dashboard.trend_rising") || "Steigend"}</th>
                        <th>{t("dashboard.trend_falling") || "Fallend"}</th>
                        <th>{t("dashboard.trend_unchanged") || "Unverändert"}</th>
                        <th>{t("single.total_count") || "Gesamt"}</th>
                        <th>{t("dashboard.average_trend") || "Ø Trend"}</th>
                    </tr>
                </thead>
                <tbody>{rows}</tbody>
            </table>
        </div>
    )
}

function TrendByCategoryRow({ rowNumber, categoryName, risingCount, fallingCount, unchangedCount, totalCount, averageTrend, activeTrendType, selectedShopId, selectedShopName, selectedLang, shopIdsForCurrentLang }) {
    const isPositive = averageTrend > 0
    const isNegative = averageTrend < 0
    const colorClass = isPositive ? "text-success" : isNegative ? "text-danger" : "text-secondary"

    // Erstelle URLs für die Trend-Zahlen
    const getTrendUrl = (trendType) => {
        const safeCategoryName = categoryName ? categoryName.replace(/\//g, '||') : ''
        const encodedCategoryName = safeCategoryName ? encodeURIComponent(safeCategoryName) : ''
        const filterProperty = trendFilterProperty(trendType)
        let url = `/pricetable/${encodedCategoryName}/Kategorie/generic/${filterProperty}`
        // Wenn ein spezifischer Shop ausgewählt ist, verwende diesen
        // Wenn "all" ausgewählt ist, keine shopId übergeben - die Preistabelle verwendet das Preset
        if (selectedShopId && selectedShopId !== "all") {
            url += `?shopId=${selectedShopId}`
        }
        return url
    }

    // Links nur anzeigen, wenn ein spezifischer Shop ausgewählt ist (nicht "all")
    const showLinks = selectedShopId && selectedShopId !== "all"

    return (
        <tr>
            <td>{rowNumber}.</td>
            <td>{categoryName}</td>
            <td className="text-success">
                {showLinks ? (
                    <a href={getTrendUrl("rising")} className="pp-price-trend-link">
                        <span className="pp-price-trend-link-span">
                            {risingCount || 0}
                            <span className="pp-price-trend-arrow">⟶</span>
                        </span>
                    </a>
                ) : (
                    <span>{risingCount || 0}</span>
                )}
            </td>
            <td className="text-danger">
                {showLinks ? (
                    <a href={getTrendUrl("falling")} className="pp-price-trend-link">
                        <span className="pp-price-trend-link-span">
                            {fallingCount || 0}
                            <span className="pp-price-trend-arrow">⟶</span>
                        </span>
                    </a>
                ) : (
                    <span>{fallingCount || 0}</span>
                )}
            </td>
            <td className="text-secondary">
                {showLinks ? (
                    <a href={getTrendUrl("unchanged")} className="pp-price-trend-link">
                        <span className="pp-price-trend-link-span">
                            {unchangedCount || 0}
                            <span className="pp-price-trend-arrow">⟶</span>
                        </span>
                    </a>
                ) : (
                    <span>{unchangedCount || 0}</span>
                )}
            </td>
            <td>{totalCount || 0}</td>
            <td className={colorClass}>
                {isPositive ? "+" : ""}
                {(averageTrend || 0).toFixed(2)}%
            </td>
        </tr>
    )
}

function TrendByShopTable({ items, i18n, activeTrendType, selectedShopId, selectedShopName, selectedLang, shopIdsForCurrentLang }) {
    const [t] = useTranslation()

    if (!items || items.length === 0) {
        return <p className="text-center text-muted">{t("single.no_results") || "Keine Daten verfügbar"}</p>
    }

    const rows = items.map((item, index) => (
        <TrendByShopRow
            key={index + "_" + JSON.stringify(item)}
            rowNumber={index + 1}
            shopName={item.shopName}
            shopId={item.shopId}
            lang={item.lang}
            risingCount={item.risingCount}
            fallingCount={item.fallingCount}
            unchangedCount={item.unchangedCount}
            totalCount={item.totalCount}
            averageTrend={item.averageTrend}
            i18n={i18n}
            activeTrendType={activeTrendType}
            selectedShopId={selectedShopId}
            selectedShopName={selectedShopName}
            selectedLang={selectedLang}
            shopIdsForCurrentLang={shopIdsForCurrentLang}
        />
    ))

    return (
        <div className="table-responsive">
            <table className="table table-striped dt-responsive">
                <thead>
                    <tr>
                        <th className={"width-10"}>#</th>
                        <th>{t("single.shop") || "Shop"}</th>
                        <th>{t("single.language")}</th>
                        <th>{t("dashboard.trend_rising") || "Steigend"}</th>
                        <th>{t("dashboard.trend_falling") || "Fallend"}</th>
                        <th>{t("dashboard.trend_unchanged") || "Unverändert"}</th>
                        <th>{t("single.total_count") || "Gesamt"}</th>
                        <th>{t("dashboard.average_trend") || "Ø Trend"}</th>
                    </tr>
                </thead>
                <tbody>{rows}</tbody>
            </table>
        </div>
    )
}

function TrendByShopRow({ rowNumber, shopName, shopId, lang, risingCount, fallingCount, unchangedCount, totalCount, averageTrend, i18n, activeTrendType, selectedShopId, selectedShopName, selectedLang, shopIdsForCurrentLang }) {
    const isPositive = averageTrend > 0
    const isNegative = averageTrend < 0
    const colorClass = isPositive ? "text-success" : isNegative ? "text-danger" : "text-secondary"

    // Erstelle URLs für die Trend-Zahlen
    const getTrendUrl = (trendType) => {
        const filterProperty = trendFilterProperty(trendType)
        let url = `/pricetable/${shopId}/Shop/generic/${filterProperty}`
        // Wenn ein spezifischer Shop ausgewählt ist, verwende diesen
        // Wenn "all" ausgewählt ist, keine shopId übergeben - die Preistabelle verwendet das Preset
        if (selectedShopId && selectedShopId !== "all") {
            url += `?shopId=${selectedShopId}`
        }
        return url
    }

    return (
        <tr>
            <td>{rowNumber}.</td>
            <td>{shopName}</td>
            <td>
                <span
                    title={countries.getName(lang, i18n.language)}
                    className={"fi fi-" + lang}
                />
            </td>
            <td className="text-success">
                <a href={getTrendUrl("rising")} className="pp-price-trend-link">
                    <span className="pp-price-trend-link-span">
                        {risingCount || 0}
                        <span className="pp-price-trend-arrow">⟶</span>
                    </span>
                </a>
            </td>
            <td className="text-danger">
                <a href={getTrendUrl("falling")} className="pp-price-trend-link">
                    <span className="pp-price-trend-link-span">
                        {fallingCount || 0}
                        <span className="pp-price-trend-arrow">⟶</span>
                    </span>
                </a>
            </td>
            <td className="text-secondary">
                <a href={getTrendUrl("unchanged")} className="pp-price-trend-link">
                    <span className="pp-price-trend-link-span">
                        {unchangedCount || 0}
                        <span className="pp-price-trend-arrow">⟶</span>
                    </span>
                </a>
            </td>
            <td>{totalCount || 0}</td>
            <td className={colorClass}>
                {isPositive ? "+" : ""}
                {(averageTrend || 0).toFixed(2)}%
            </td>
        </tr>
    )
}


export { PPDonutTrendBox, TrendOverview, TrendByCategoryTable, TrendByShopTable, trendFilterProperty, trendColorClass }
